import { useState } from "react";
import { BedDouble, Copy, Navigation, Pencil, Moon, AlertTriangle } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { CalendarButton } from "./CalendarButton";
import { copyText } from "./DriverCard";
import { useTrip, useUI } from "@/lib/store";
import { fmtDayMonth } from "@/lib/dates";

export function HotelCard({ hotel, mode = "full" }) {
  const { organiser, updateHotel, trip } = useTrip();
  const { showDriver } = useUI();
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(hotel.notes || "");
  const dest = trip.destinations.find((d) => d.id === hotel.destinationId);
  const sym = trip.settings.currencySymbol || "$";

  return (
    <article data-testid={`hotel-card-${hotel.id}`} className="card-soft overflow-hidden">
      {mode === "full" && (
        <div className="relative h-44 w-full overflow-hidden">
          <img src={hotel.image} alt={hotel.city} className="h-full w-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F2620]/80 via-[#0F2620]/20 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 text-[var(--parchment)]">
            <p className="eyebrow !text-[var(--parchment)]/80">{hotel.city} · {dest?.chinese}</p>
            <h3 className="font-display text-3xl font-semibold leading-tight">{hotel.name}</h3>
          </div>
        </div>
      )}
      <div className="p-4">
        {mode !== "full" && (
          <div className="mb-2 flex items-start justify-between gap-3">
            <div>
              <p className="eyebrow flex items-center gap-1"><Moon className="h-3 w-3" /> Tonight's hotel · {hotel.city}</p>
              <h3 className="font-display text-2xl font-semibold leading-tight">{hotel.name}</h3>
            </div>
            <StatusBadge status={hotel.status} />
          </div>
        )}
        {hotel.subtitle && <p className="text-sm text-[var(--ink-muted)]">{hotel.subtitle}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className="inline-flex items-center gap-1 rounded-full bg-[var(--parchment-2)] px-3 py-1 font-medium"><BedDouble className="h-3.5 w-3.5" /> {hotel.nights} night{hotel.nights > 1 ? "s" : ""} · {fmtDayMonth(hotel.checkIn)} → {fmtDayMonth(hotel.checkOut)}</span>
          <span className="rounded-full bg-[var(--parchment-2)] px-3 py-1 font-medium">{hotel.roomSummary}</span>
          {mode === "full" && <StatusBadge status={hotel.status} />}
        </div>
        <p data-testid={`hotel-chinese-${hotel.id}`} className="font-cn mt-4 text-xl font-semibold leading-snug text-[var(--jade-deep)]">{hotel.addressChinese}</p>
        <p className="mt-1 text-sm leading-relaxed text-[var(--ink-muted)]">{hotel.address}</p>

        {mode === "full" && hotel.rooms?.some((r) => r.occupants) && (
          <div className="mt-4 space-y-1.5">
            <p className="eyebrow">Room allocation</p>
            {hotel.rooms.map((r) => (
              <div key={r.label} className="flex items-baseline justify-between rounded-xl bg-[var(--parchment-2)] px-3 py-2 text-sm">
                <span><span className="font-semibold">{r.occupants || r.label}</span> <span className="text-[var(--ink-muted)]">· {r.type}</span></span>
                {organiser && r.cost != null && <span className="font-mono text-xs">{sym}{r.cost.toFixed(2)}</span>}
              </div>
            ))}
          </div>
        )}

        {organiser && (
          <div className="mt-4 rounded-2xl border border-dashed border-[var(--plum)]/30 bg-[var(--plum-soft)]/40 p-3 text-sm">
            <div className="flex items-center justify-between">
              <p className="eyebrow !text-[var(--plum)]">Organiser</p>
              <button data-testid={`hotel-edit-notes-${hotel.id}`} onClick={() => setEditing((e) => !e)} className="tap inline-flex items-center gap-1 text-xs font-semibold text-[var(--plum)]"><Pencil className="h-3 w-3" /> {editing ? "Close" : "Edit notes"}</button>
            </div>
            <p className="mt-1">Cost: <span className="font-mono">{sym}{hotel.costTotal?.toFixed(2)}</span> total{hotel.costEachNote ? ` · ${hotel.costEachNote}` : ""}</p>
            <p data-testid={`hotel-payment-${hotel.id}`} className={`mt-1 font-semibold ${hotel.paymentStatus === "unpaid" ? "text-rose-700" : ""}`}>
              {hotel.paymentStatus === "unpaid" ? <span className="inline-flex items-center gap-1"><AlertTriangle className="h-4 w-4" /> PAYMENT ACTION REQUIRED — not paid per spreadsheet</span> : hotel.paymentStatus === "paid" ? "Payment: marked paid" : "Payment: not stated in source"}
            </p>
            {editing ? (
              <div className="mt-2 space-y-2">
                <textarea data-testid={`hotel-notes-input-${hotel.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="w-full rounded-xl border bg-white p-2 text-sm" />
                <div className="flex gap-2">
                  <select data-testid={`hotel-payment-select-${hotel.id}`} value={hotel.paymentStatus} onChange={(e) => updateHotel(hotel.id, { paymentStatus: e.target.value, status: e.target.value === "unpaid" ? "action_required" : hotel.status === "action_required" ? "planned" : hotel.status })} className="rounded-xl border bg-white px-2 py-1.5 text-xs">
                    <option value="not_stated">Payment not stated</option><option value="unpaid">Unpaid</option><option value="paid">Paid</option>
                  </select>
                  <select data-testid={`hotel-status-select-${hotel.id}`} value={hotel.status} onChange={(e) => updateHotel(hotel.id, { status: e.target.value })} className="rounded-xl border bg-white px-2 py-1.5 text-xs">
                    <option value="planned">Planned</option><option value="confirmed">Confirmed</option><option value="tbc">TBC</option><option value="action_required">Action required</option>
                  </select>
                  <button data-testid={`hotel-save-notes-${hotel.id}`} onClick={() => { updateHotel(hotel.id, { notes }); setEditing(false); }} className="tap ml-auto rounded-full bg-[var(--plum)] px-4 py-1.5 text-xs font-semibold text-white">Save</button>
                </div>
              </div>
            ) : hotel.notes ? <p className="mt-1 whitespace-pre-wrap text-[var(--ink)]">{hotel.notes}</p> : null}
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid={`show-driver-btn-${hotel.id}`} onClick={() => showDriver(hotel)} className="tap inline-flex items-center gap-2 rounded-full bg-[var(--jade)] px-4 py-2.5 text-sm font-semibold text-[var(--parchment)]"><Navigation className="h-4 w-4" /> Show driver</button>
          <button data-testid={`copy-address-btn-${hotel.id}`} onClick={() => copyText(`${hotel.name}\n${hotel.addressChinese}\n${hotel.address}`, "Address copied")} className="tap inline-flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 text-sm font-semibold"><Copy className="h-4 w-4" /> Copy address</button>
          <CalendarButton kind="hotel" item={hotel} label="Add stay" testId={`calendar-hotel-btn-${hotel.id}`} />
        </div>
      </div>
    </article>
  );
}
